import { useState } from 'react';
import { formaterDate, formaterDateHeure } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille } from '../composants/Pastille';
import { LIBELLE_PANIER, TON_PANIER, panierTransmissible } from '../composants/statutPanier';
import { LienFiche } from '../composants/LienAction';
import { ApercuPanier } from '../composants/ApercuPanier';
import { Nombre } from '../composants/Nombre';
import { BarreFiltres, FiltreChoix, Pagination, type PageDe } from '../composants/Filtres';

interface Panier {
  id: string;
  reference: string;
  canal: string;
  datePanier: string;
  statut: string;
  transmisLe: string | null;
  ordres: { id: string; reference: string; statut: string }[];
}

/** Ordres validés qui n'attendent plus qu'un panier, par canal. */
interface AConstituer {
  canal: string;
  nombre: number;
  montantEstime: string;
}

const CANAUX = [
  { code: 'BOURSE', libelle: 'Société de bourse — actions' },
  { code: 'SALLE_MARCHES', libelle: 'Salle des marchés — BDT' },
  { code: 'OPCVM', libelle: 'Sociétés de gestion — OPCVM' },
];

export function Paniers() {
  const { aRole } = useAuth();
  const [canal, setCanal] = useState('');
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const requete = new URLSearchParams({ page: String(page), taille: '50' });
  if (canal) requete.set('canal', canal);
  if (statut) requete.set('statut', statut);
  const { donnees, chargement, erreur, recharger } = useRessource<PageDe<Panier>>(
    `/paniers?${requete.toString()}`,
  );
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [apercu, setApercu] = useState<(typeof CANAUX)[number] | null>(null);

  const paniers = donnees?.elements ?? [];

  function filtrer(action: () => void) {
    setPage(1);
    action();
  }
  const peutGerer = aRole('MIDDLE_BO', 'RESP_GESTION');
  const aConstituer = useRessource<AConstituer[]>(peutGerer ? '/paniers/a-constituer' : null);
  const totalAConstituer = (aConstituer.donnees ?? []).reduce((s, a) => s + a.nombre, 0);

  async function agir(appel: () => Promise<unknown>) {
    setErreurAction(null);
    setOccupe(true);
    try {
      await appel();
      await recharger();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Panier>[] = [
    {
      cle: 'reference',
      entete: 'Référence',
      num: true,
      rendu: (p) => <LienFiche to={`/paniers/${p.id}`}>{p.reference}</LienFiche>,
    },
    { cle: 'canal', entete: 'Canal', rendu: (p) => p.canal.replace('_', ' ').toLowerCase() },
    { cle: 'date', entete: 'Date', num: true, rendu: (p) => formaterDate(p.datePanier) },
    { cle: 'ordres', entete: 'Ordres', num: true, rendu: (p) => String(p.ordres.length) },
    {
      cle: 'transmis',
      entete: 'Transmis le',
      num: true,
      rendu: (p) => formaterDateHeure(p.transmisLe),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (p) => (
        <Pastille ton={TON_PANIER[p.statut] ?? 'neutre'}>
          {LIBELLE_PANIER[p.statut] ?? p.statut}
        </Pastille>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (p) => (
        <span className="rangee-actions">
          <a className="bouton" href={`/api/v1/paniers/${p.id}/export`}>
            Exporter
          </a>
          {peutGerer && panierTransmissible(p.statut) && (
            <button
              className="bouton bouton--fantome"
              disabled={occupe}
              onClick={() => void agir(() => api.post(`/paniers/${p.id}/transmettre`))}
            >
              Marquer transmis
            </button>
          )}
          {/* Un panier servi en totalité se clôture seul ; celui-ci reste partiel.
              Pas de clôture tant qu'un ordre attend la situation qui le dénouera. */}
          {peutGerer &&
            p.statut === 'TRANSMIS' &&
            !p.ordres.some((o) => o.statut === 'TRANSMIS') && (
              <button
                className="bouton bouton--fantome"
                disabled={occupe}
                onClick={() => {
                  if (
                    window.confirm(
                      `Clôturer ${p.reference} ? Les reliquats de ses ordres ne seront plus rapprochés avec les situations suivantes.`,
                    )
                  )
                    void agir(() => api.post(`/paniers/${p.id}/cloturer`));
                }}
              >
                Clôturer
              </button>
            )}
        </span>
      ),
    },
  ];

  return (
    <>
      <p className="entete__fil">Exécution</p>
      <div className="entete">
        <h1>Paniers</h1>
        <span className="entete__ref">{donnees ? `${donnees.total} paniers` : ''}</span>
      </div>

      {(erreur || erreurAction) && (
        <BandeauAlerte nature="refus" titre="Opération refusée">
          {erreurAction ?? messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {peutGerer && (
        <Bloc
          titre="Constituer un panier"
          meta={
            aConstituer.donnees
              ? `${totalAConstituer} ordre(s) validé(s) à constituer`
              : 'aperçu des ordres validés du canal'
          }
        >
          <section className="cles" aria-label="Ordres validés à constituer">
            {CANAUX.map((c) => {
              const attente = aConstituer.donnees?.find((a) => a.canal === c.code);
              const nombre = attente?.nombre ?? 0;
              return (
                <div className="cle" key={c.code}>
                  <span className="surtitre">{c.libelle}</span>
                  <div className="cle__valeur">
                    {attente ? nombre : '—'}
                    <small>ordre(s) validé(s) à constituer</small>
                  </div>
                  <div className="appoint">
                    {nombre > 0 ? (
                      <>
                        Montant estimé <Nombre valeur={attente!.montantEstime} nature="devise" />
                      </>
                    ) : (
                      'Rien à constituer'
                    )}
                  </div>
                  <button
                    type="button"
                    className="bouton bouton--compact espace-avant-serre"
                    disabled={occupe || nombre === 0}
                    onClick={() => {
                      setErreurAction(null);
                      setApercu(c);
                    }}
                  >
                    Constituer
                  </button>
                </div>
              );
            })}
          </section>
        </Bloc>
      )}

      <Bloc titre="Paniers" meta={chargement ? 'chargement…' : 'du plus récent au plus ancien'}>
        <BarreFiltres
          actifs={canal !== '' || statut !== ''}
          onRetirer={() =>
            filtrer(() => {
              setCanal('');
              setStatut('');
            })
          }
        >
          <FiltreChoix
            libelle="Canal"
            tous="Tous les canaux"
            valeur={canal}
            onChange={(v) => filtrer(() => setCanal(v))}
            options={CANAUX.map((c) => [c.code, c.libelle] as const)}
          />
          <FiltreChoix
            libelle="Statut"
            tous="Tous les statuts"
            valeur={statut}
            onChange={(v) => filtrer(() => setStatut(v))}
            options={Object.entries(LIBELLE_PANIER)}
          />
        </BarreFiltres>
        <Tableau colonnes={colonnes} lignes={paniers} cle={(p) => p.id} vide="Aucun panier." />
        {donnees && (
          <Pagination
            page={donnees.page}
            taille={donnees.taille}
            total={donnees.total}
            onPage={setPage}
          />
        )}
      </Bloc>

      {apercu && (
        <ApercuPanier
          canal={apercu.code}
          libelle={apercu.libelle}
          onFermer={() => setApercu(null)}
          onGenere={() => {
            setApercu(null);
            void recharger();
            void aConstituer.recharger();
          }}
        />
      )}
    </>
  );
}
